// Fast alle Texturen entstehen zur Laufzeit (VIEW-001). Geladen wird nur, was
// `gfx/sprites.ts` im Manifest nennt: Schiffe, Bauteile, Brocken, Adern, die
// zehn Gegnerblaetter, die Drohnen, die Sonden und die Gegnergeschosse.
//
// Was hier steht, ist deshalb zweierlei: alles Uebrige — Icons, Nebel, Sterne,
// Erz-Drops, Rakete, Partikel — und der **Rueckfall**. Der Rueckfall malt
// in **denselben** Texturschluessel wie das PNG und ueberspringt jeden Schluessel,
// den der Ladevorgang schon gefuellt hat. Damit bleibt das Spiel startfaehig,
// wenn eine Datei fehlt, ohne dass die Szene zwei Faelle kennen muesste.
//
// Bei den beiden Rastern gehoert das **Raster selbst** zum Rueckfall: eine
// Leinwand ohne Frames haette genau einen, und die Szene bekaeme beim Zeichnen
// von Frame 7 ein leeres Bild — ohne Fehler, ohne Meldung.
//
// Der Unterschied zum Vorlaeufer ist die **Vorberechnung**: dort wurde jeder
// Brocken pro Frame als Polygon gezeichnet. Phaser zeichnet Sprites um
// Groessenordnungen schneller als Pfade, also entstehen hier beim Start ein
// paar Dutzend kleine Texturen, und danach kostet ein Brocken nichts mehr.
//
// Nebel und Sterne sind ebenfalls vorgerenderte, **nahtlos kachelbare**
// Flaechen. Beides pro Frame zu rechnen waere der teuerste Teil des Bildes —
// und man merkt es auf dem eigenen Rechner nicht.

import Phaser from 'phaser';

import { TAU } from '../core/math';
import { rngFrom } from '../core/rng';
import { builds, planets, resources } from '../data/balancing';
import { baseModule } from './base';
import { ICON_GRID, ICON_KEYS, drawIcon } from './icons';
import { planetPixels, planetTextureKey } from './planet';
import {
  BASE_COLS,
  BASE_FRAME,
  BASE_ROWS,
  BASE_SHEET_KEY,
  BOLT_COLS,
  BOLT_FRAME,
  BOLT_ROWS,
  BOLT_SHEET_KEY,
  DRONE_COLS,
  DRONE_FRAME,
  DRONE_ROWS,
  DRONE_SHEET_KEY,
  ENEMY_FALLBACK_FRAME,
  ENEMY_FALLBACK_KEY,
  FIND_COLS,
  FIND_FRAME,
  FIND_ROWS,
  FIND_SHEET_KEY,
  ENEMY_SHEETS,
  PROBE_COLS,
  PROBE_FRAME,
  PROBE_ROWS,
  PROBE_SHEET_KEY,
  ROCK_COLS,
  ROCK_FRAME,
  ROCK_ROWS,
  ROCK_SHEET_KEY,
  ROCK_VARIANTS,
  SHIP_CLASSES,
  SHIP_COLS,
  SHIP_FRAME,
  SHIP_LEVELS,
  SHIP_PER_CLASS,
  SHIP_ROWS,
  VEIN_COLS,
  VEIN_FRAME,
  VEIN_ROWS,
  VEIN_SHEET_KEY,
  VEIN_VARIANTS,
  enemySheetKey,
  shipClassKey,
} from './sprites';

export const ICON_TEXTURE_SIZE = 64;
export const NEBULA_TILE = 512;
export const STAR_TILE = 512;

export const iconTextureKey = (name: string): string => `icon:${name}`;
export const dropTextureKey = (ore: string): string => `drop:${ore}`;
/**
 * **Eine** Nebelkachel fuer alle Zonen.
 *
 * Vorher gab es eine je Zonenfarbe — bei identischer Saat, also dieselben
 * Wolken in sechs Farben. Genau deshalb konnte der Nebel nur hart umschalten:
 * ein Kachelbild traegt eine Textur, keinen Verlauf. Als **Struktur** in Weiss
 * mit Alpha ist die Farbe ein Tint, und ein Tint laesst sich mischen.
 */
export const NEBULA_KEY = 'nebula';

function canvas(scene: Phaser.Scene, key: string, width: number, height: number): CanvasRenderingContext2D {
  if (scene.textures.exists(key)) scene.textures.remove(key);
  const texture = scene.textures.createCanvas(key, width, height);
  if (!texture) throw new Error(`Textur ${key} konnte nicht angelegt werden`);
  return texture.getContext();
}

function commit(scene: Phaser.Scene, key: string): void {
  const texture = scene.textures.get(key);
  if (texture.source[0]) (texture as Phaser.Textures.CanvasTexture).refresh();
}

/** Alle Icons als Texturen — der Technologiebaum zeichnet sie als Sprites. */
export function buildIconTextures(scene: Phaser.Scene): void {
  for (const name of ICON_KEYS) {
    const key = iconTextureKey(name);
    const ctx = canvas(scene, key, ICON_TEXTURE_SIZE, ICON_TEXTURE_SIZE);
    ctx.imageSmoothingEnabled = false;
    drawIcon(ctx, name, 0, 0, ICON_TEXTURE_SIZE);
    commit(scene, key);
  }
}

/**
 * Rueckfall fuer das Bauteilraster — der sichtbare Takt der Progression (FG-45).
 *
 * Gezeichnet statt fotografiert, in **denselben** Schluessel und dasselbe
 * Raster wie `base.png`. Eine Ausbaustufe ohne Form bleibt dabei still leer: an
 * der Station stuende dann eine bezahlte Stufe, die niemand sieht. Ein `throw`
 * waere hier falsch — die Tabellen kommen vom Server, und ein Server, der dem
 * Client voraus ist, darf keinen Browser am Start hindern. Dagegen steht das
 * Gate, nicht ein Ersatzsymbol.
 */
export function buildBaseSheetFallback(scene: Phaser.Scene): void {
  if (scene.textures.exists(BASE_SHEET_KEY)) return;
  const ctx = canvas(scene, BASE_SHEET_KEY, BASE_COLS * BASE_FRAME, BASE_ROWS * BASE_FRAME);
  ctx.imageSmoothingEnabled = false;
  for (const entry of builds()) {
    const modul = baseModule(entry.id);
    if (!modul || modul.frame === null) continue;
    ctx.save();
    ctx.translate(
      (modul.frame % BASE_COLS) * BASE_FRAME + BASE_FRAME / 2,
      Math.floor(modul.frame / BASE_COLS) * BASE_FRAME + BASE_FRAME / 2,
    );
    modul.draw(ctx, BASE_FRAME);
    ctx.restore();
  }
  commit(scene, BASE_SHEET_KEY);
  rastern(scene, BASE_SHEET_KEY, BASE_FRAME, BASE_COLS, BASE_ROWS);
}

/** Texturschluessel eines Bauteils **ohne** Platz im Raster. */
export const basePartKey = (id: string): string => `basepart:${id}`;

/**
 * Bauteile, fuer die das Blatt keine Zelle mehr hat — je eine eigene Textur.
 *
 * `base.png` ist ein 4x4-Raster mit sechzehn Zellen; die Ausbaukette hat seit
 * der Navigationskette neunzehn Stufen. Das Raster zu vergroessern hiesse, ein
 * **Bild** neu zu erzeugen — keine Codeaenderung, und bis dahin waere die
 * Alternative eine bezahlte Stufe, die niemand sieht. Genau die verhindert
 * FG-45.
 *
 * Gezeichnet wird dieselbe `draw`, die ohnehin als Rueckfall dasteht — es gibt
 * also keine zweite Formquelle. Anders als das Blatt entsteht sie **immer**,
 * auch wenn `base.png` geladen hat: es gibt dort schlicht nichts zu laden.
 */
export function buildBasePartTextures(scene: Phaser.Scene): void {
  for (const entry of builds()) {
    const modul = baseModule(entry.id);
    if (!modul || modul.frame !== null) continue;
    const key = basePartKey(entry.id);
    if (scene.textures.exists(key)) continue;
    const ctx = canvas(scene, key, BASE_FRAME, BASE_FRAME);
    ctx.imageSmoothingEnabled = false;
    ctx.save();
    ctx.translate(BASE_FRAME / 2, BASE_FRAME / 2);
    modul.draw(ctx, BASE_FRAME);
    ctx.restore();
    commit(scene, key);
  }
}

/**
 * Zerlegt eine Leinwand in Frames — der Rueckfall traegt dasselbe Raster wie das
 * PNG.
 *
 * Ohne diesen Schritt haette die Ersatztextur genau **einen** Frame, und die
 * Szene bekaeme beim Zeichnen von Frame 7 ein leeres Bild: kein Fehler, keine
 * Meldung, nur ein Feld ohne Brocken.
 */
function rastern(scene: Phaser.Scene, key: string, frame: number, cols: number, rows: number): void {
  const textur = scene.textures.get(key);
  for (let i = 0; i < cols * rows; i++) {
    textur.add(i, 0, (i % cols) * frame, Math.floor(i / cols) * frame, frame, frame);
  }
}

/**
 * Rueckfall fuer das Brockenraster: gezeichnete Formen statt fotografierter.
 *
 * Er malt in **denselben** Texturschluessel wie das PNG und ueberspringt ihn,
 * sobald der Ladevorgang ihn gefuellt hat — dieselbe Regel wie beim Schiff. Das
 * Feld sieht schlichter aus und funktioniert vollstaendig.
 */
export function buildRockSheetFallback(scene: Phaser.Scene): void {
  if (scene.textures.exists(ROCK_SHEET_KEY)) return;
  const ctx = canvas(scene, ROCK_SHEET_KEY, ROCK_COLS * ROCK_FRAME, ROCK_ROWS * ROCK_FRAME);
  for (let variant = 0; variant < ROCK_VARIANTS; variant++) {
    ctx.save();
    ctx.translate((variant % ROCK_COLS) * ROCK_FRAME, Math.floor(variant / ROCK_COLS) * ROCK_FRAME);
    drawRock(ctx, variant);
    ctx.restore();
  }
  commit(scene, ROCK_SHEET_KEY);
  rastern(scene, ROCK_SHEET_KEY, ROCK_FRAME, ROCK_COLS, ROCK_ROWS);
}

function drawRock(ctx: CanvasRenderingContext2D, variant: number): void {
  // Graustufen, wie im Foto: die Erzfarbe kommt in beiden Faellen erst als Tint,
  // und ein reicher Brocken entsteht aus derselben Form mit dunklerer Toenung.
  const rng = rngFrom(1000 + variant * 97);
  const mid = ROCK_FRAME / 2;
  const radius = mid - 6;
  const corners = 7 + Math.floor(rng() * 4);

  ctx.save();
  ctx.translate(mid, mid);
  ctx.beginPath();
  for (let i = 0; i < corners; i++) {
    const a = (i / corners) * TAU;
    const r = radius * (0.78 + rng() * 0.34);
    const x = Math.cos(a) * r;
    const y = Math.sin(a) * r;
    if (i) ctx.lineTo(x, y); else ctx.moveTo(x, y);
  }
  ctx.closePath();

  ctx.fillStyle = '#dad6d0';
  ctx.fill();
  ctx.lineWidth = 5;
  ctx.strokeStyle = '#8e8a84';
  ctx.stroke();

  // Kein gerichtetes Licht: der Brocken dreht sich, und ein eingebackener
  // Schatten drehte mit. Nur eine flache Delle, damit die Form nicht platt wirkt.
  ctx.save();
  ctx.clip();
  ctx.fillStyle = 'rgba(0,0,0,.12)';
  ctx.beginPath();
  ctx.arc(radius * 0.45, radius * 0.5, radius * 1.05, 0, TAU);
  ctx.fill();
  ctx.restore();

  for (let i = 0; i < 8; i++) {
    const a = rng() * TAU;
    const d = Math.sqrt(rng()) * radius * 0.62;
    const r = (1.4 + rng() * 1.8) * (ROCK_FRAME / 40);
    ctx.fillStyle = '#9a958e';
    ctx.beginPath(); ctx.arc(Math.cos(a) * d, Math.sin(a) * d, r, 0, TAU); ctx.fill();
    ctx.fillStyle = 'rgba(255,255,255,.35)';
    ctx.fillRect(Math.cos(a) * d - r * 0.5, Math.sin(a) * d - r * 0.7, r * 0.6, r * 0.5);
  }
  ctx.restore();
}

/**
 * Rueckfall fuer das Adernraster: zwoelf Netze von grob nach fein.
 *
 * Die Reihenfolge ist Vertrag, nicht Zierde — `veinFrameOf` waehlt den Frame aus
 * dem Rang der Dichte. Ein Rueckfall in beliebiger Reihenfolge gaebe dem
 * dichtesten Erz das groebste Bild.
 */
export function buildVeinSheetFallback(scene: Phaser.Scene): void {
  if (scene.textures.exists(VEIN_SHEET_KEY)) return;
  const ctx = canvas(scene, VEIN_SHEET_KEY, VEIN_COLS * VEIN_FRAME, VEIN_ROWS * VEIN_FRAME);
  for (let i = 0; i < VEIN_VARIANTS; i++) {
    ctx.save();
    ctx.translate((i % VEIN_COLS) * VEIN_FRAME, Math.floor(i / VEIN_COLS) * VEIN_FRAME);
    drawVeins(ctx, i);
    ctx.restore();
  }
  commit(scene, VEIN_SHEET_KEY);
  rastern(scene, VEIN_SHEET_KEY, VEIN_FRAME, VEIN_COLS, VEIN_ROWS);
}

function drawVeins(ctx: CanvasRenderingContext2D, index: number): void {
  const rng = rngFrom(7000 + index * 131);
  const mid = VEIN_FRAME / 2;
  // Von grob und sparsam nach fein und dicht — dieselbe Steigerung wie im Foto.
  const anteil = index / Math.max(1, VEIN_VARIANTS - 1);
  const arme = 5 + Math.round(anteil * 5);
  const tiefe = 2 + Math.round(anteil * 2);
  const staerke = 5 - anteil * 3.2;

  ctx.save();
  ctx.translate(mid, mid);
  ctx.strokeStyle = '#ffffff';
  ctx.lineCap = 'round';

  const ast = (x: number, y: number, winkel: number, laenge: number, breite: number, rest: number): void => {
    const zx = x + Math.cos(winkel) * laenge;
    const zy = y + Math.sin(winkel) * laenge;
    ctx.lineWidth = Math.max(0.7, breite);
    ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(zx, zy); ctx.stroke();
    if (rest <= 0) return;
    for (const seite of [-1, 1]) {
      ast(zx, zy, winkel + seite * (0.35 + rng() * 0.45), laenge * 0.6, breite * 0.55, rest - 1);
    }
  };

  for (let i = 0; i < arme; i++) {
    const a = (i / arme) * TAU + rng() * 0.4;
    ast(0, 0, a, mid * 0.44, staerke, tiefe);
  }
  ctx.restore();
}

/** Erz-Drops: eine kleine Kugel je Ressource. */
export function buildDropTextures(scene: Phaser.Scene): void {
  for (const ore of resources()) {
    const key = dropTextureKey(ore.id);
    const size = 32;
    const ctx = canvas(scene, key, size, size);
    const mid = size / 2;
    ctx.fillStyle = ore.dark;
    ctx.beginPath(); ctx.arc(mid, mid, mid - 2, 0, TAU); ctx.fill();
    ctx.fillStyle = ore.color;
    ctx.beginPath(); ctx.arc(mid, mid, mid - 5, 0, TAU); ctx.fill();
    ctx.fillStyle = 'rgba(255,255,255,.7)';
    ctx.fillRect(mid - 5, mid - 7, 5, 5);
    commit(scene, key);
  }
}

/**
 * Rueckfall fuer die fuenf Schiffsblaetter — nur fuer Klassen, deren PNG fehlt.
 *
 * Dieselbe Rumpfform wie frueher, je Level groesser. Der Aufstieg bleibt damit
 * auch ohne Bilder sichtbar — als Groesse, nicht als Aufbauten.
 */
function buildShipFallback(scene: Phaser.Scene): void {
  for (let klasse = 0; klasse < SHIP_CLASSES; klasse++) {
    const key = shipClassKey(klasse);
    if (scene.textures.exists(key)) continue;
    const ctx = canvas(scene, key, SHIP_COLS * SHIP_FRAME, SHIP_ROWS * SHIP_FRAME);
    for (let platz = 0; platz < SHIP_PER_CLASS; platz++) {
      const stufe = klasse * SHIP_PER_CLASS + platz;
      ctx.save();
      ctx.translate(
        (platz % SHIP_COLS) * SHIP_FRAME + SHIP_FRAME / 2,
        Math.floor(platz / SHIP_COLS) * SHIP_FRAME + SHIP_FRAME / 2,
      );
      // Motivbreite von rund 24 auf 52 Pixel ueber alle 50 Stufen. Der Faktor
      // im Spiel (`shipRenderScale`) kommt obendrauf — hier geht es nur darum,
      // dass die Ersatzform ihren Frame ordentlich fuellt.
      const scale = 1.4 + (stufe / (SHIP_LEVELS - 1)) * 1.6;
      ctx.scale(scale, scale);
      // Der Rumpf liegt zwischen x = -7 und x = 10, seine Mitte also bei 1,5.
      // Ohne diese Verschiebung saesse der Drehpunkt neben dem Schiff.
      ctx.translate(-1.5, 0);
      ctx.fillStyle = '#cdc6b8';
      ctx.beginPath();
      ctx.moveTo(10, 0); ctx.lineTo(1, -6); ctx.lineTo(-7, -5);
      ctx.lineTo(-7, 5); ctx.lineTo(1, 6);
      ctx.closePath(); ctx.fill();
      ctx.strokeStyle = '#2b3038'; ctx.lineWidth = 1; ctx.stroke();
      ctx.fillStyle = '#5fd0e6'; ctx.fillRect(-1, -2.5, 5, 5);
      ctx.fillStyle = '#8e8778';
      ctx.fillRect(-7, -6.5, 4, 2); ctx.fillRect(-7, 4.5, 4, 2);
      ctx.restore();
    }
    commit(scene, key);
    rastern(scene, key, SHIP_FRAME, SHIP_COLS, SHIP_ROWS);
  }
}

/**
 * Ein schlichter Rumpf, nach rechts zeigend — die Grundform aller Rueckfaelle
 * unten.
 *
 * Sie steht einmal, weil Gegner, Drohne und Sonde dieselbe Aufgabe haben: bei
 * fehlender Datei etwas zeigen, das nach Maschine aussieht und in die richtige
 * Richtung blickt. Drei Fassungen davon liefen still auseinander.
 */
function drawHull(
  ctx: CanvasRenderingContext2D, laenge: number, breite: number, farbe: string,
): void {
  const nase = laenge / 2;
  const heck = -laenge / 2;
  const halb = breite / 2;
  ctx.fillStyle = farbe;
  ctx.beginPath();
  ctx.moveTo(nase, 0);
  ctx.lineTo(nase * 0.1, -halb);
  ctx.lineTo(heck, -halb * 0.8);
  ctx.lineTo(heck, halb * 0.8);
  ctx.lineTo(nase * 0.1, halb);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = '#2b3038'; ctx.lineWidth = 1; ctx.stroke();
}

/**
 * Rueckfall fuer die zehn Gegnerblaetter — je Blatt einer, in dessen Raster.
 *
 * Er unterscheidet die Arten **nicht**: das taeten dreizehn gezeichnete
 * Silhouetten nur behaupten, und sie waeren die zweite Formquelle, die still
 * veraltet. Was er sichert, ist die Zusage aus FG-40 — das Spiel startet ohne
 * jede einzelne Datei, und der Gegner bleibt sichtbar und richtig gross. Die
 * **Farbe** trennt die Raenge dabei weiterhin (`hostileTint`).
 */
export function buildEnemySheetFallbacks(scene: Phaser.Scene): void {
  for (const [id, [cols, rows, frame]] of Object.entries(ENEMY_SHEETS)) {
    const key = enemySheetKey(id);
    if (scene.textures.exists(key)) continue;
    const ctx = canvas(scene, key, cols * frame, rows * frame);
    ctx.imageSmoothingEnabled = false;
    for (let i = 0; i < cols * rows; i++) {
      const rng = rngFrom(7000 + i * 131);
      ctx.save();
      ctx.translate((i % cols) * frame + frame / 2, Math.floor(i / cols) * frame + frame / 2);
      drawHull(ctx, frame * (0.62 + rng() * 0.16), frame * (0.34 + rng() * 0.2), '#cdc6b8');
      ctx.fillStyle = '#ff6a3c';
      ctx.fillRect(frame * 0.06, -frame * 0.04, frame * 0.08, frame * 0.08);
      ctx.restore();
    }
    commit(scene, key);
    rastern(scene, key, frame, cols, rows);
  }
}

/**
 * Der Rueckfall fuer eine Gegnerart **ohne** Blatt.
 *
 * Er entsteht **immer**, auch wenn alle Dateien geladen haben: es gibt dafuer
 * schlicht nichts zu laden. Sein Fall ist ein Server, der dem Client voraus ist
 * — dann steht dort ein schlichter Rumpf statt eines unsichtbaren Gegners, der
 * einen trotzdem beschiesst.
 */
export function buildEnemyFallbackTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(ENEMY_FALLBACK_KEY)) return;
  const ctx = canvas(scene, ENEMY_FALLBACK_KEY, ENEMY_FALLBACK_FRAME, ENEMY_FALLBACK_FRAME);
  ctx.imageSmoothingEnabled = false;
  ctx.translate(ENEMY_FALLBACK_FRAME / 2, ENEMY_FALLBACK_FRAME / 2);
  drawHull(ctx, ENEMY_FALLBACK_FRAME * 0.7, ENEMY_FALLBACK_FRAME * 0.44, '#cdc6b8');
  commit(scene, ENEMY_FALLBACK_KEY);
}

/**
 * Rueckfall fuer das Drohnenblatt: Zeile ist die Schiffsklasse, Spalte der Platz.
 *
 * Die Zeile waechst, weil der Schwarm mit dem Schiff waechst — dasselbe, was das
 * Bild erzaehlt, nur als Groesse.
 */
export function buildDroneSheetFallback(scene: Phaser.Scene): void {
  if (scene.textures.exists(DRONE_SHEET_KEY)) return;
  const ctx = canvas(scene, DRONE_SHEET_KEY, DRONE_COLS * DRONE_FRAME, DRONE_ROWS * DRONE_FRAME);
  ctx.imageSmoothingEnabled = false;
  for (let i = 0; i < DRONE_COLS * DRONE_ROWS; i++) {
    const zeile = Math.floor(i / DRONE_COLS);
    const wachstum = 0.34 + (zeile / (DRONE_ROWS - 1)) * 0.36;
    ctx.save();
    ctx.translate(
      (i % DRONE_COLS) * DRONE_FRAME + DRONE_FRAME / 2,
      zeile * DRONE_FRAME + DRONE_FRAME / 2,
    );
    drawHull(ctx, DRONE_FRAME * wachstum, DRONE_FRAME * wachstum * 0.7, '#cdc6b8');
    ctx.fillStyle = '#5fd0e6';
    ctx.fillRect(-DRONE_FRAME * 0.04, -DRONE_FRAME * 0.04, DRONE_FRAME * 0.08, DRONE_FRAME * 0.08);
    ctx.restore();
  }
  commit(scene, DRONE_SHEET_KEY);
  rastern(scene, DRONE_SHEET_KEY, DRONE_FRAME, DRONE_COLS, DRONE_ROWS);
}

/** Rueckfall fuer das Sondenblatt — Rumpf mit Schuessel, sonst wie die Drohne. */
export function buildProbeSheetFallback(scene: Phaser.Scene): void {
  if (scene.textures.exists(PROBE_SHEET_KEY)) return;
  const ctx = canvas(scene, PROBE_SHEET_KEY, PROBE_COLS * PROBE_FRAME, PROBE_ROWS * PROBE_FRAME);
  ctx.imageSmoothingEnabled = false;
  for (let i = 0; i < PROBE_COLS * PROBE_ROWS; i++) {
    ctx.save();
    ctx.translate(
      (i % PROBE_COLS) * PROBE_FRAME + PROBE_FRAME / 2,
      Math.floor(i / PROBE_COLS) * PROBE_FRAME + PROBE_FRAME / 2,
    );
    drawHull(ctx, PROBE_FRAME * 0.4, PROBE_FRAME * 0.3, '#cdc6b8');
    ctx.fillStyle = '#8e8778';
    ctx.fillRect(PROBE_FRAME * 0.2, -PROBE_FRAME * 0.16, PROBE_FRAME * 0.06, PROBE_FRAME * 0.32);
    ctx.restore();
  }
  commit(scene, PROBE_SHEET_KEY);
  rastern(scene, PROBE_SHEET_KEY, PROBE_FRAME, PROBE_COLS, PROBE_ROWS);
}

/**
 * Rueckfall fuer die Gegnergeschosse.
 *
 * Weiss gezeichnet, damit die Szene sie je Rang einfaerbt — heller Kern, weicher
 * Schweif. Dieselbe Form wie der `bolt`-Strich davor, nur als Raster.
 */
export function buildBoltSheetFallback(scene: Phaser.Scene): void {
  if (scene.textures.exists(BOLT_SHEET_KEY)) return;
  const ctx = canvas(scene, BOLT_SHEET_KEY, BOLT_COLS * BOLT_FRAME, BOLT_ROWS * BOLT_FRAME);
  ctx.imageSmoothingEnabled = false;
  for (let i = 0; i < BOLT_COLS * BOLT_ROWS; i++) {
    const dicke = 3 + (i % 3) * 2;
    ctx.save();
    ctx.translate((i % BOLT_COLS) * BOLT_FRAME, Math.floor(i / BOLT_COLS) * BOLT_FRAME);
    ctx.fillStyle = 'rgba(255,255,255,0.45)';
    ctx.fillRect(4, (BOLT_FRAME - dicke) / 2, 16, dicke);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(16, (BOLT_FRAME - dicke) / 2 - 1, 12, dicke + 2);
    ctx.restore();
  }
  commit(scene, BOLT_SHEET_KEY);
  rastern(scene, BOLT_SHEET_KEY, BOLT_FRAME, BOLT_COLS, BOLT_ROWS);
}

/**
 * Rueckfall fuer das Fundraster — **sechs eigene Formen**, nicht sechsmal dieselbe.
 *
 * Er malt in **denselben** Texturschluessel wie das PNG und ueberspringt ihn,
 * sobald der Ladevorgang ihn gefuellt hat (Gate FG-40, dieselbe Regel wie beim
 * Schiff). Und er legt seine **Frames selbst an**: ein Rueckfall ohne Raster
 * gaebe der Textur genau einen Frame, und die Szene zeichnete das ganze Blatt
 * als eine Kiste.
 *
 * Fuenf Kisten und ein Geraet. Vorher war es **eine** blaue Kiste fuer alles,
 * obwohl die Namen eine Bake, einen Kanister, einen Wandschrank und vier
 * Container beschreiben — wer 20 000 Meter nach einem Hinweis fliegt, soll am
 * Bild sehen, dass er richtig ist.
 *
 * In **Farbe** und nicht in Weiss zum Einfaerben: der Hinweis nennt die erste
 * „die blaue Kiste", und ein Tint waere die zweite Stelle, an der diese Farbe
 * steht.
 */
export function buildFindSheetFallback(scene: Phaser.Scene): void {
  if (scene.textures.exists(FIND_SHEET_KEY)) return;
  const f = FIND_FRAME;
  const ctx = canvas(scene, FIND_SHEET_KEY, f * FIND_COLS, f * FIND_ROWS);
  ctx.imageSmoothingEnabled = false;

  const zeichner = [
    zeichneKiste, zeichneBake, zeichneKanister, zeichneSchrank, zeichneDepot, zeichneArm,
  ];
  for (let i = 0; i < zeichner.length; i++) {
    ctx.save();
    ctx.translate((i % FIND_COLS) * f, Math.floor(i / FIND_COLS) * f);
    zeichner[i](ctx, f);
    ctx.restore();
  }

  commit(scene, FIND_SHEET_KEY);
  rastern(scene, FIND_SHEET_KEY, f, FIND_COLS, FIND_ROWS);
}

/** Die blaue Frachtkiste — gerade Kanten, Deckelspalt, zwei Spannbaender. */
function zeichneKiste(ctx: CanvasRenderingContext2D, f: number): void {
  const m = f * 0.18;
  ctx.fillStyle = '#1d3f6b';
  ctx.fillRect(m, m + 4, f - 2 * m, f - 2 * m - 8);
  ctx.fillStyle = '#2f6bb0';
  ctx.fillRect(m + 2, m + 6, f - 2 * m - 4, f - 2 * m - 12);
  ctx.fillStyle = '#14294a';
  ctx.fillRect(m + 2, f * 0.4, f - 2 * m - 4, 3);
  ctx.fillStyle = '#9fc4ea';
  ctx.fillRect(m + 8, m + 6, 4, f - 2 * m - 12);
  ctx.fillRect(f - m - 12, m + 6, 4, f - 2 * m - 12);
  ctx.fillStyle = '#ffd98a';
  ctx.fillRect(f / 2 - 3, f * 0.38, 6, 7);
}

/** Die stumme Bake — ein Mast mit Ring, der Hohlraum als Versteck. */
function zeichneBake(ctx: CanvasRenderingContext2D, f: number): void {
  ctx.fillStyle = '#3a4a5c';
  ctx.fillRect(f / 2 - 4, f * 0.16, 8, f * 0.68);
  ctx.fillStyle = '#5d7a92';
  ctx.fillRect(f / 2 - 2, f * 0.18, 4, f * 0.64);
  ctx.strokeStyle = '#8fb3c9';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(f / 2, f * 0.34, f * 0.2, 0, Math.PI * 2);
  ctx.stroke();
  ctx.fillStyle = '#243244';
  ctx.fillRect(f / 2 - 6, f * 0.58, 12, 12);          // der offene Hohlraum
  ctx.fillStyle = '#ff8f6a';
  ctx.fillRect(f / 2 - 2, f * 0.14, 4, 4);
}

/** Der geflickte Kanister — Druckbehaelter mit Schweissnaehten. */
function zeichneKanister(ctx: CanvasRenderingContext2D, f: number): void {
  ctx.fillStyle = '#6b5a3a';
  ctx.beginPath();
  ctx.ellipse(f / 2, f / 2, f * 0.22, f * 0.32, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#9c8452';
  ctx.beginPath();
  ctx.ellipse(f / 2 - 2, f / 2 - 2, f * 0.18, f * 0.28, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#c9b184';
  ctx.lineWidth = 2;
  for (let i = 0; i < 3; i++) {
    ctx.beginPath();
    ctx.moveTo(f * 0.3, f * (0.34 + i * 0.14));
    ctx.lineTo(f * 0.7, f * (0.32 + i * 0.14));
    ctx.stroke();
  }
  ctx.fillStyle = '#2a2018';
  ctx.fillRect(f * 0.62, f * 0.38, 6, 10);            // der Riss von aussen
}

/** Der Werkzeugschrank — hoch, zwei Tueren, verzurrt. */
function zeichneSchrank(ctx: CanvasRenderingContext2D, f: number): void {
  ctx.fillStyle = '#33474a';
  ctx.fillRect(f * 0.24, f * 0.1, f * 0.52, f * 0.8);
  ctx.fillStyle = '#4b6b70';
  ctx.fillRect(f * 0.27, f * 0.13, f * 0.46, f * 0.74);
  ctx.fillStyle = '#223033';
  ctx.fillRect(f / 2 - 1, f * 0.13, 2, f * 0.74);     // Tuerspalt
  ctx.fillStyle = '#d9c07a';
  ctx.fillRect(f / 2 - 8, f * 0.46, 5, 3);
  ctx.fillRect(f / 2 + 3, f * 0.46, 5, 3);
  ctx.strokeStyle = '#8fa6ab';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(f * 0.2, f * 0.32);
  ctx.lineTo(f * 0.8, f * 0.32);
  ctx.moveTo(f * 0.2, f * 0.68);
  ctx.lineTo(f * 0.8, f * 0.68);
  ctx.stroke();
}

/** Das Depot — vier Container im Verbund. */
function zeichneDepot(ctx: CanvasRenderingContext2D, f: number): void {
  const toene = ['#8a5a2a', '#2a5a8a', '#5a8a3a', '#7a3a5a'];
  const w = f * 0.4;
  const h = f * 0.38;
  for (let i = 0; i < 4; i++) {
    const cx = f * 0.08 + (i % 2) * w;
    const cy = f * 0.1 + Math.floor(i / 2) * h;
    ctx.fillStyle = '#1b1b1b';
    ctx.fillRect(cx, cy, w, h);
    ctx.fillStyle = toene[i];
    ctx.fillRect(cx + 2, cy + 2, w - 4, h - 4);
    ctx.fillStyle = 'rgba(0,0,0,.35)';
    for (let r = 0; r < 3; r++) ctx.fillRect(cx + 4 + r * 6, cy + 3, 2, h - 6);
  }
  ctx.strokeStyle = '#c9c9c9';
  ctx.lineWidth = 2;
  ctx.strokeRect(f * 0.06, f * 0.08, w * 2 + 4, h * 2 + 4);
}

/** Der Verladearm — Hydraulik mit Klaue, nach rechts gerichtet wie das Schiff. */
function zeichneArm(ctx: CanvasRenderingContext2D, f: number): void {
  ctx.fillStyle = '#4a4a52';
  ctx.fillRect(f * 0.08, f * 0.44, f * 0.5, f * 0.12);
  ctx.fillStyle = '#7c7c88';
  ctx.fillRect(f * 0.1, f * 0.46, f * 0.46, f * 0.06);
  ctx.fillStyle = '#d9a441';
  ctx.fillRect(f * 0.3, f * 0.42, f * 0.08, f * 0.16);   // Gelenk
  // Die Klaue: zwei Backen, leicht geoeffnet.
  ctx.fillStyle = '#9aa0aa';
  ctx.beginPath();
  ctx.moveTo(f * 0.58, f * 0.5);
  ctx.lineTo(f * 0.86, f * 0.3);
  ctx.lineTo(f * 0.92, f * 0.38);
  ctx.lineTo(f * 0.66, f * 0.5);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(f * 0.58, f * 0.5);
  ctx.lineTo(f * 0.86, f * 0.7);
  ctx.lineTo(f * 0.92, f * 0.62);
  ctx.lineTo(f * 0.66, f * 0.5);
  ctx.closePath();
  ctx.fill();
}

/** Schiff, Rakete, Partikel — kleine Formen, einmal gezeichnet. */
export function buildShipTextures(scene: Phaser.Scene): void {
  buildShipFallback(scene);

  const missile = canvas(scene, 'missile', 16, 8);
  missile.translate(8, 4);
  missile.fillStyle = '#ffd06a'; missile.fillRect(-7, -1.5, 4, 3);
  missile.fillStyle = '#e8e2d4'; missile.fillRect(-3, -2, 8, 4);
  missile.fillStyle = '#ff6a3c'; missile.fillRect(3, -2, 3, 4);
  commit(scene, 'missile');

  const spark = canvas(scene, 'spark', 8, 8);
  spark.fillStyle = '#ffffff';
  spark.fillRect(2, 2, 4, 4);
  commit(scene, 'spark');

  const beam = canvas(scene, 'beam', 8, 8);
  beam.fillStyle = '#ffffff';
  beam.fillRect(0, 0, 8, 8);
  commit(scene, 'beam');
}

/** Sternenfeld: eine nahtlos kachelbare Flaeche, einmal erzeugt. */
export function buildStarTexture(scene: Phaser.Scene, layer: number): string {
  const key = `stars:${layer}`;
  const ctx = canvas(scene, key, STAR_TILE, STAR_TILE);
  const rng = rngFrom(1337 + layer * 7919);
  const count = layer === 0 ? 150 : 90;
  for (let i = 0; i < count; i++) {
    const x = Math.floor(rng() * STAR_TILE);
    const y = Math.floor(rng() * STAR_TILE);
    const size = rng() < 0.86 ? 1 : 2;
    ctx.fillStyle = `rgba(255,255,255,${(0.25 + rng() * 0.75).toFixed(3)})`;
    ctx.fillRect(x, y, size, size);
  }
  commit(scene, key);
  return key;
}

/**
 * Die Nebelkachel — nahtlos, weil sie sich selbst umschliesst.
 *
 * Weiss mit Alpha: die Farbe kommt als Tint (im Shader je Bildpunkt, im
 * Rueckfall je Bild). Die Struktur ist damit in jeder Zone dieselbe — was sie
 * vorher auch schon war, denn alle sechs Fassungen zogen aus derselben Saat.
 */
export function buildNebulaTexture(scene: Phaser.Scene): string {
  if (scene.textures.exists(NEBULA_KEY)) return NEBULA_KEY;
  const ctx = canvas(scene, NEBULA_KEY, NEBULA_TILE, NEBULA_TILE);
  const rng = rngFrom(4242);
  for (let i = 0; i < 26; i++) {
    const bx = rng() * NEBULA_TILE;
    const by = rng() * NEBULA_TILE;
    const r = 50 + rng() * 130;
    const alpha = 0.05 + rng() * 0.12;
    for (let oy = -1; oy <= 1; oy++) {
      for (let ox = -1; ox <= 1; ox++) {
        const x = bx + ox * NEBULA_TILE;
        const y = by + oy * NEBULA_TILE;
        if (x + r < 0 || x - r > NEBULA_TILE || y + r < 0 || y - r > NEBULA_TILE) continue;
        const grad = ctx.createRadialGradient(x, y, 0, x, y, r);
        grad.addColorStop(0, `rgba(255,255,255,${alpha})`);
        grad.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = grad;
        ctx.fillRect(x - r, y - r, r * 2, r * 2);
      }
    }
  }
  commit(scene, NEBULA_KEY);
  return NEBULA_KEY;
}

/**
 * Die Planetenkugeln (Akt III) — **bei Bedarf**, nicht beim Start.
 *
 * Fünf Kugeln sind knapp eine Million Texel. Wer in Akt I spielt, braucht
 * keine davon; die Weltszene ruft das hier, sobald sie im Heimatsystem steht.
 * Wer schon eine Textur hat, wird übersprungen — der Aufruf ist billig genug,
 * um ihn bei jedem Ärenwechsel zu wiederholen.
 */
export function ensurePlanetTextures(scene: Phaser.Scene): void {
  planets().forEach((planet, index) => {
    const key = planetTextureKey(planet.id);
    if (scene.textures.exists(key)) return;
    const { size, data } = planetPixels(planet, index);
    const ctx = canvas(scene, key, size, size);
    const bild = ctx.createImageData(size, size);
    bild.data.set(data);
    ctx.putImageData(bild, 0, 0);
    commit(scene, key);
  });
}

/**
 * Alles beim Start erzeugen. Danach kostet Zeichnen keine Pfade mehr.
 *
 * Die Brocken haengen an keinem `webgl`-Fall mehr: sie sind Sprites, und Sprites
 * zeichnet auch der Canvas-Renderer. Der Rueckfall greift nur noch, wenn eine
 * Datei fehlt.
 */
export function buildAllTextures(scene: Phaser.Scene): void {
  buildIconTextures(scene);
  buildBaseSheetFallback(scene);
  buildBasePartTextures(scene);
  buildRockSheetFallback(scene);
  buildVeinSheetFallback(scene);
  buildEnemySheetFallbacks(scene);
  buildEnemyFallbackTexture(scene);
  buildDroneSheetFallback(scene);
  buildProbeSheetFallback(scene);
  buildBoltSheetFallback(scene);
  buildFindSheetFallback(scene);
  buildDropTextures(scene);
  buildShipTextures(scene);
  buildStarTexture(scene, 0);
  buildStarTexture(scene, 1);
  buildNebulaTexture(scene);
}

export { ICON_GRID };
